import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

// Server-side paging/search/sort for the HQ users table, backed by the
// `user_directory` view and `user_counts()` RPC (users_directory_migration.sql).
//
// Both hooks degrade instead of breaking: if the migration has not been run
// the view/function are missing, `available` flips to false and the caller
// falls back to the in-memory list it already had. That keeps HQ working on a
// database that is one deploy behind the app.

// Any failure — the migration not being applied, a permissions problem, a
// network blip — drops the caller back to the in-memory list it already had.
// An operator staring at an empty table is worse than one paging through a
// slightly staler list, so there is deliberately no error state here; a
// reload retries from scratch.

function transformRow(row) {
  return {
    id: row.id,
    name: row.name,
    fullName: row.name,
    username: row.username,
    email: row.email,
    role: row.role,
    organizationId: row.organization_id,
    organizationName: row.organization_name,
    branchId: row.branch_id,
    branchName: row.branch_name,
    isActive: row.is_active,
    mustChangePassword: row.must_change_password,
    createdAt: row.created_at,
    joinedAt: row.created_at ? new Date(row.created_at) : null,
    lastSeenAt: row.last_seen_at ? new Date(row.last_seen_at) : null,
  }
}

// Table sort keys → view columns. Sorting on organisation/branch is why the
// view exists: the names live in other tables and can't be ordered on
// client-side once results are paged.
const SORT_COLUMNS = {
  user: 'name',
  role: 'role',
  org: 'organization_name',
  branch: 'branch_name',
  status: 'is_active',
  email: 'email',
  lastSeen: 'last_seen_at',
  joined: 'created_at',
}

export function useUserDirectory(currentUser, { page, pageSize, search, sort, groupByOrg, enabled = true }) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(enabled)
  const [available, setAvailable] = useState(true)
  // Bumped by refetch() so mutations can force a reload of the current page.
  const [nonce, setNonce] = useState(0)
  const reqId = useRef(0)

  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    if (!currentUser || !enabled || !available) return
    let cancelled = false
    const id = ++reqId.current

    async function run() {
      setLoading(true)
      let query = supabase.from('user_directory').select('*', { count: 'exact' })

      const term = (search || '').trim()
      if (term) {
        // Escape PostgREST's or() delimiters so a comma or paren in the search
        // box can't rewrite the filter expression.
        const safe = term.replace(/[,()\\]/g, ' ').trim()
        if (safe) {
          const like = `%${safe}%`
          query = query.or(
            `name.ilike.${like},username.ilike.${like},email.ilike.${like},organization_name.ilike.${like}`
          )
        }
      }

      // Grouped mode orders by organisation so each page's groups stay
      // contiguous; otherwise honour the clicked column, defaulting to the
      // role-then-name order the panel has always used.
      if (groupByOrg) {
        query = query.order('organization_name', { ascending: true, nullsFirst: false }).order('name')
      } else if (sort?.key && SORT_COLUMNS[sort.key]) {
        query = query.order(SORT_COLUMNS[sort.key], { ascending: sort.dir !== 'desc', nullsFirst: false })
          .order('name')
      } else {
        query = query.order('role').order('name')
      }

      const from = page * pageSize
      const { data, error, count } = await query.range(from, from + pageSize - 1)
      if (cancelled || id !== reqId.current) return

      if (error) {
        setAvailable(false)
        setLoading(false)
        return
      }
      setRows((data || []).map(transformRow))
      setTotal(count ?? 0)
      setLoading(false)
    }

    run()
    return () => { cancelled = true }
  }, [currentUser?.id, enabled, available, page, pageSize, search, sort?.key, sort?.dir, groupByOrg, nonce])

  return { rows, total, loading, available, refetch }
}

// Per-organization aggregates so HQ can show totals without downloading every
// profile. Returns a Map keyed by organization id (null-org accounts land
// under the 'none' key) plus the summed totals.
export function useUserCounts(currentUser, { enabled = true } = {}) {
  const [byOrg, setByOrg] = useState(new Map())
  const [totals, setTotals] = useState({ total: 0, active: 0, admins: 0 })
  const [available, setAvailable] = useState(true)
  const [nonce, setNonce] = useState(0)

  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    if (!currentUser || !enabled || !available) return
    let cancelled = false

    async function run() {
      const { data, error } = await supabase.rpc('user_counts')
      if (cancelled) return
      if (error) {
        setAvailable(false)
        return
      }
      const map = new Map()
      const sum = { total: 0, active: 0, admins: 0 }
      for (const row of data || []) {
        const entry = {
          total: Number(row.total) || 0,
          active: Number(row.active) || 0,
          admins: Number(row.admins) || 0,
        }
        map.set(row.organization_id || 'none', entry)
        sum.total += entry.total
        sum.active += entry.active
        sum.admins += entry.admins
      }
      setByOrg(map)
      setTotals(sum)
    }

    run()
    return () => { cancelled = true }
  }, [currentUser?.id, enabled, available, nonce])

  return { byOrg, totals, available, refetch }
}
